// Article List Component
import { Star, Circle, CheckCircle } from 'lucide-react';
import { type Article } from '@/lib/api';
import { formatRelativeTime, getArticleExcerpt } from '@/lib/utils';
import { Skeleton } from '@/components/Skeleton';
import { Badge } from '@/components/Badge';
import { cn } from '@/lib/utils';

interface ArticleListProps {
  articles: Article[];
  isLoading: boolean;
  selectedId: number | null;
  onSelectArticle: (id: number) => void;
  title?: string;
  subtitle?: string;
}

export default function ArticleList({
  articles,
  isLoading,
  selectedId,
  onSelectArticle,
  title = 'All Articles',
  subtitle,
}: ArticleListProps) {
  if (isLoading) {
    return (
      <div className="h-full overflow-y-auto scrollbar-thin">
        {/* Header skeleton */}
        <div className="sticky top-0 z-10 border-b border-border bg-background/95 p-4 backdrop-blur">
          <Skeleton className="mb-2 h-6 w-32" />
          <Skeleton className="h-4 w-48" />
        </div>
        {/* Article list skeleton */}
        <div className="p-4 space-y-3">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <div key={i} className="rounded-lg border border-border p-4">
              <div className="mb-2 flex items-start justify-between gap-2">
                <Skeleton className="h-5 flex-1" />
                <Skeleton className="h-4 w-4 rounded-full" />
              </div>
              <Skeleton className="mb-2 h-4 w-full" />
              <Skeleton className="mb-2 h-3 w-3/4" />
              <Skeleton className="h-3 w-24" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (articles.length === 0) {
    return (
      <div className="h-full overflow-y-auto scrollbar-thin">
        {/* Header */}
        <div className="sticky top-0 z-10 border-b border-border bg-background/95 p-4 backdrop-blur">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h2 className="text-lg font-semibold">{title}</h2>
              <p className="text-sm text-muted-foreground">{subtitle}</p>
            </div>
          </div>
        </div>
        {/* Empty state */}
        <div className="flex h-full items-center justify-center p-8 text-center">
          <div>
            <Circle className="mx-auto mb-4 h-12 w-12 text-muted-foreground opacity-50" />
            <p className="text-muted-foreground">No articles to display</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {title === 'All Articles' 
                ? 'Add some feeds to get started'
                : `No articles found in ${title.toLowerCase()}`
              }
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto scrollbar-thin">
      {/* Header */}
      <div className="sticky top-0 z-10 border-b border-border bg-background/95 p-4 backdrop-blur">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h2 className="text-lg font-semibold">{title}</h2>
            <p className="text-sm text-muted-foreground">
              {subtitle}
              {articles.length > 0 && (
                <span className="ml-1">
                  • {articles.length} {articles.length === 1 ? 'item' : 'items'}
                </span>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Article Items */}
      <div className="p-4 space-y-3">
        {articles.map((article) => (
          <ArticleItem
            key={article.id}
            article={article}
            selected={article.id === selectedId}
            onClick={() => onSelectArticle(article.id)}
          />
        ))}
      </div>
    </div>
  );
}

function ArticleItem({
  article,
  selected,
  onClick,
}: {
  article: Article;
  selected: boolean;
  onClick: () => void;
}) {
  const excerpt = getArticleExcerpt(article.content_html);

  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full rounded-lg border border-border p-4 text-left transition-colors hover:bg-accent',
        selected && 'border-primary bg-accent',
        !article.read && 'bg-primary/5'
      )}
    >
      <div className="mb-2 flex items-start justify-between gap-2">
        <h3 className={cn(
          'text-sm font-medium leading-tight',
          !article.read && 'font-semibold'
        )}>
          {article.title}
        </h3>
        <div className="flex shrink-0 items-center gap-1">
          {article.starred === 1 && (
            <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
          )}
          {article.read === 1 ? (
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          ) : (
            <Circle className="h-4 w-4 text-primary" />
          )}
        </div>
      </div>

      {excerpt && (
        <p className="mb-2 line-clamp-2 text-xs text-muted-foreground">
          {excerpt}
        </p>
      )}

      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <span>{formatRelativeTime(article.published_at)}</span>
        {article.summary_ai && (
          <Badge variant="secondary" className="text-xs">AI Summary</Badge>
        )}
        {article.priority_score > 70 && (
          <Badge variant="default" className="text-xs">Priority</Badge>
        )}
      </div>
    </button>
  );
}
